/**
 * แบบประเมินพนักงาน (เฟส 2–3 ของ requirement HR ต.ค. 2026) — single source
 * ใช้ร่วมกันทั้ง worker (คำนวณ/ตรวจ) และ liff (เรนเดอร์ฟอร์ม)
 * อ้างอิงไฟล์จริง: แบบประเมินตนเอง / หัวหน้างาน / เพื่อนร่วมงาน (สเกล 1–5)
 */

export type EvalRole = 'self' | 'supervisor' | 'peer';

export interface EvalItem {
  key: string;
  label: string;
}
export interface EvalTemplate {
  role: EvalRole;
  title: string;
  items: EvalItem[]; // หัวข้อให้คะแนน 1–5
  openQuestions: EvalItem[]; // คำถามปลายเปิด
  maxScore: number; // items.length * 5
  hasOutcome: boolean; // ฟอร์มหัวหน้ามีสรุปผล
}

export const EVAL_SCALE = [
  { value: 5, label: 'ดีเยี่ยม' },
  { value: 4, label: 'ดีมาก' },
  { value: 3, label: 'ดี' },
  { value: 2, label: 'ควรปรับปรุง' },
  { value: 1, label: 'ต้องปรับปรุงเร่งด่วน' },
] as const;

export const EVAL_OUTCOMES = [
  { key: 'pass', label: 'ผ่านการประเมิน' },
  { key: 'pass_conditional', label: 'ผ่านการประเมินแบบมีเงื่อนไข' },
  { key: 'needs_improve', label: 'ต้องปรับปรุง' },
  { key: 'fail', label: 'ไม่ผ่านการประเมิน' },
] as const;

export type EvalOutcome = (typeof EVAL_OUTCOMES)[number]['key'];

function mk(role: EvalRole, title: string, items: string[], open: string[], hasOutcome = false): EvalTemplate {
  return {
    role,
    title,
    items: items.map((label, i) => ({ key: `s${i + 1}`, label })),
    openQuestions: open.map((label, i) => ({ key: `q${i + 1}`, label })),
    maxScore: items.length * 5,
    hasOutcome,
  };
}

export const EVAL_TEMPLATES: Record<EvalRole, EvalTemplate> = {
  self: mk(
    'self',
    'แบบประเมินตนเอง',
    [
      'ความรับผิดชอบต่องานที่ได้รับมอบหมาย',
      'ความตรงต่อเวลาและการรักษาวินัย',
      'คุณภาพของงานที่ส่งมอบ',
      'การทำงานเป็นทีม',
      'การสื่อสารกับเพื่อนร่วมงาน',
      'ความคิดริเริ่มและการแก้ไขปัญหา',
      'ความพร้อมในการเรียนรู้และพัฒนาตนเอง',
      'การปฏิบัติตามกฎระเบียบบริษัท',
    ],
    ['ผลงานที่ภาคภูมิใจในรอบการประเมิน', 'สิ่งที่ต้องการพัฒนาปรับปรุง', 'เป้าหมายในการทำงานรอบถัดไป', 'ความคิดเห็นเพิ่มเติม'],
  ),
  supervisor: mk(
    'supervisor',
    'แบบประเมินหัวหน้างาน',
    [
      'คุณภาพของงาน',
      'ปริมาณงานที่รับผิดชอบ',
      'ความรับผิดชอบต่อหน้าที่',
      'ความตรงต่อเวลาและวินัยในการทำงาน',
      'การปฏิบัติตามกฎระเบียบของบริษัท',
      'การทำงานร่วมกับผู้อื่น',
      'การสื่อสารและประสานงาน',
      'การแก้ไขปัญหาเฉพาะหน้า',
      'ความคิดริเริ่มสร้างสรรค์',
      'ความสามารถในการเรียนรู้และพัฒนา',
    ],
    ['จุดเด่นของพนักงาน', 'สิ่งที่ควรพัฒนาเพิ่มเติม', 'แผนพัฒนาในรอบถัดไป'],
    true,
  ),
  peer: mk(
    'peer',
    'แบบประเมินเพื่อนร่วมงาน',
    [
      'ความรับผิดชอบต่อหน้าที่',
      'ความร่วมมือในการทำงานเป็นทีม',
      'ความสุภาพและการให้เกียรติผู้อื่น',
      'การสื่อสารและประสานงาน',
      'ความน่าเชื่อถือและไว้วางใจได้',
      'การช่วยเหลือและสนับสนุนทีม',
      'ความคิดสร้างสรรค์และการแก้ปัญหา',
      'ทัศนคติในการทำงาน',
    ],
    ['จุดเด่นของเพื่อนร่วมงาน', 'สิ่งที่ควรพัฒนาเพิ่มเติม', 'ข้อเสนอแนะเพื่อการพัฒนา'],
  ),
};

/** รวมคะแนนจาก answers.scores ตาม template (ข้ามค่าที่ไม่ใช่ 1–5) */
export function evalTotalScore(role: EvalRole, scores: Record<string, number> | null | undefined): number {
  const tmpl = EVAL_TEMPLATES[role];
  let total = 0;
  for (const it of tmpl.items) {
    const v = scores?.[it.key];
    if (typeof v === 'number' && v >= 1 && v <= 5) total += v;
  }
  return total;
}
